"use server"

import { sdk } from "@lib/config"
import { sortProducts } from "@lib/util/sort-products"
import { HttpTypes } from "@medusajs/types"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"
import { getAuthHeaders, getCacheOptions } from "./cookies"
import { getRegion, retrieveRegion } from "./regions"

export const listProducts = async ({
  pageParam = 1,
  queryParams,
  countryCode,
  regionId,
}: {
  pageParam?: number
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductListParams
  countryCode?: string
  regionId?: string
}): Promise<{
  response: { products: HttpTypes.StoreProduct[]; count: number }
  nextPage: number | null
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductListParams
}> => {
  if (!countryCode && !regionId) {
    throw new Error("Country code or region ID is required")
  }

  const limit = queryParams?.limit || 12
  const _pageParam = Math.max(pageParam, 1)
  const offset = _pageParam === 1 ? 0 : (_pageParam - 1) * limit

  let region: HttpTypes.StoreRegion | undefined | null

  if (countryCode) {
    region = await getRegion(countryCode)
  } else {
    region = await retrieveRegion(regionId!)
  }

  if (!region) {
    return {
      response: { products: [], count: 0 },
      nextPage: null,
    }
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  const cacheOptions = await getCacheOptions("products")

  // Use tagged cache when available (logged-in users); otherwise revalidate
  // every 60 s so inventory changes from the admin panel reflect promptly.
  const next = Object.keys(cacheOptions).length
    ? cacheOptions
    : { revalidate: 60 }

  return sdk.client
    .fetch<{ products: HttpTypes.StoreProduct[]; count: number }>(
      `/store/products`,
      {
        method: "GET",
        query: {
          limit,
          offset,
          // region_id alone selects the currency: Medusa resolves
          // calculated_price server-side from region.currency_code.
          //
          // Do NOT add currency_code here. GET /store/products has no such query
          // field in Medusa 2.13.5 and rejects unrecognized fields outright
          // ("Invalid request: Unrecognized fields: 'currency_code'"), which 404s
          // every product page. Verified against the store product validators.
          region_id: region?.id,
          fields:
            "*images,*variants.calculated_price,+variants.inventory_quantity,*variants.manage_inventory,*variants.images,+metadata,+tags,",
          ...queryParams,
        },
        headers,
        next,
      }
    )
    .then(({ products, count }) => {
      const nextPage = count > offset + limit ? pageParam + 1 : null

      return {
        response: {
          products,
          count,
        },
        nextPage: nextPage,
        queryParams,
      }
    })
}

export type ProductCustomFields = {
  manufacturer: string | null
  age_group: string | null
  sleeve_length: string | null
  season: string | null
  team: string | null
  color: string | null
}

/**
 * Fetches a product's football-specific attributes (manufacturer, season,
 * colour, team, sleeve length, age group), resolved to display labels.
 * Used by the PDP's Product Description accordion spec list.
 */
export const getProductCustomFields = async (
  productId: string
): Promise<ProductCustomFields | null> => {
  try {
    const { custom_fields } = await sdk.client.fetch<{
      custom_fields: ProductCustomFields | null
    }>(`/store/products/${productId}/custom-fields`, {
      method: "GET",
      next: { revalidate: 300 },
    })

    return custom_fields
  } catch (error) {
    console.error(`Failed to load custom fields for product ${productId}:`, error)
    return null
  }
}

/**
 * This will fetch 100 products to the Next.js cache and sort them based on the sortBy parameter.
 * It will then return the paginated products based on the page and limit parameters.
 */
export const listProductsWithSort = async ({
  page = 0,
  queryParams,
  sortBy = "created_at",
  countryCode,
}: {
  page?: number
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductParams
  sortBy?: SortOptions
  countryCode: string
}): Promise<{
  response: { products: HttpTypes.StoreProduct[]; count: number }
  nextPage: number | null
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductParams
}> => {
  const limit = queryParams?.limit || 12

  const {
    response: { products, count },
  } = await listProducts({
    pageParam: 0,
    queryParams: {
      ...queryParams,
      limit: 100,
    },
    countryCode,
  })

  const sortedProducts = sortProducts(products, sortBy)

  const pageParam = (page - 1) * limit

  const nextPage = count > pageParam + limit ? pageParam + limit : null

  const paginatedProducts = sortedProducts.slice(pageParam, pageParam + limit)

  return {
    response: {
      products: paginatedProducts,
      count,
    },
    nextPage,
    queryParams,
  }
}
